import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@napaicp/ui'
import { Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  isSupportedLocale,
  persistLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@/lib/i18n'

const SHORT_LABEL: Record<SupportedLocale, string> = { 'zh-TW': '中', en: 'En' }

/**
 * Compact zh-TW / English switcher for global navigation.
 *
 * Selecting a language persists it to localStorage so it survives a reload,
 * and — when the caller passes an authenticated user whose profile carries a
 * locale — reports the choice back so the caller can decide whether to sync
 * it. This component itself never calls the API: syncing to the backend is
 * out of scope until a dedicated endpoint exists.
 */
export function LanguageSwitcher({ onChange }: { onChange?: (locale: SupportedLocale) => void }) {
  const { i18n, t } = useTranslation('nav')
  const current = isSupportedLocale(i18n.language) ? i18n.language : 'zh-TW'

  function select(locale: SupportedLocale): void {
    persistLocale(locale)
    void i18n.changeLanguage(locale)
    onChange?.(locale)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={t('language')} className="gap-1.5 px-2">
          <Globe className="size-4" aria-hidden="true" />
          <span>{SHORT_LABEL[current]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SUPPORTED_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            aria-current={locale === current ? 'true' : undefined}
            onSelect={() => select(locale)}
          >
            {locale === 'zh-TW' ? t('languages.zhTW') : t('languages.en')}
            {locale === current ? <span className="ml-auto text-xs">✓</span> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
